/**
 * Curated AI model catalogue.
 *
 * OpenRouter exposes hundreds of models. Ordinary org members should only ever
 * see the handful we have priced, benchmarked and support; the full catalogue
 * stays available to superusers and org admins for evaluation.
 *
 * `creditCost` is the credit debit for one AI response on that model, mirroring
 * the `PlanModelPricing.creditCost` rows that must be seeded per plan version.
 * The costs below are grounded in measured OpenRouter spend (2026-08-19, three
 * representative clinic prompts each) expressed as a multiple of the default
 * model, then rounded to a tier.
 */

/** One selectable model and what a reply on it costs in credits. */
export interface SelectableModel {
	readonly creditCost: number;
	readonly id: string;
	readonly label: string;
	/** Measured cost per reply as a multiple of the default model. */
	readonly measuredCostMultiple: number;
}

/** The default model every org starts on: cheapest per reply that still tool-calls well. */
export const DEFAULT_SELECTABLE_MODEL = "openai/gpt-5.6-luna";

/**
 * The models a non-admin org member may choose. Ordered cheapest first so the
 * selector's default ordering matches ascending credit cost.
 */
export const SELECTABLE_MODELS: readonly SelectableModel[] = [
	{
		id: "google/gemini-3.1-flash-lite",
		label: "Gemini 3.1 Flash Lite",
		creditCost: 1,
		measuredCostMultiple: 0.6,
	},
	{
		id: DEFAULT_SELECTABLE_MODEL,
		label: "GPT-5.6 Luna",
		creditCost: 1,
		measuredCostMultiple: 1,
	},
	{
		id: "anthropic/claude-sonnet-5",
		label: "Claude Sonnet 5",
		creditCost: 9,
		measuredCostMultiple: 9,
	},
	{
		id: "openai/gpt-5.6-sol",
		label: "GPT-5.6 Sol",
		creditCost: 13,
		measuredCostMultiple: 12.7,
	},
	{
		id: "x-ai/grok-4.6",
		label: "Grok 4.6",
		creditCost: 26,
		measuredCostMultiple: 26.4,
	},
	{
		id: "moonshotai/kimi-k3",
		label: "Kimi K3",
		creditCost: 50,
		measuredCostMultiple: 49.5,
	},
];

/** Model ids a non-admin org member may choose. */
export const SELECTABLE_MODEL_IDS: readonly string[] = SELECTABLE_MODELS.map(
	(model) => model.id
);

/** Credit cost for one reply on `modelId`, or `null` when it is not curated. */
export function selectableModelCreditCost(modelId: string): number | null {
	return (
		SELECTABLE_MODELS.find((model) => model.id === modelId)?.creditCost ?? null
	);
}

/**
 * Whether `modelId` may be selected by a caller with this authority. Superusers
 * and org admins keep the full catalogue; everyone else is held to the curated
 * list.
 */
export function canSelectModel(
	modelId: string,
	authority: { isSuperuser: boolean; isOrgAdmin: boolean }
): boolean {
	if (authority.isSuperuser || authority.isOrgAdmin) {
		return true;
	}
	return SELECTABLE_MODEL_IDS.includes(modelId);
}
